import type { FormEvent } from 'react'
import { useEffect, useState } from 'react'
import { usePageHeader } from '../components/layout/PageHeaderContext'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { MaruyaApiError } from '../lib/maruya-api'
import {
  getRpaCredentialsStatus,
  putRpaCredentials,
  type RpaCredentialsStatus,
} from '../lib/rpa-credentials-api'

export function RpaCredentialsPage() {
  usePageHeader('設定', 'RPA連携アカウント')

  const [status, setStatus] = useState<RpaCredentialsStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [editing, setEditing] = useState(false)
  const [loginId, setLoginId] = useState('')
  const [password, setPassword] = useState('')
  const [subdomain, setSubdomain] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  function loadStatus() {
    setLoading(true)
    setLoadError(null)
    getRpaCredentialsStatus()
      .then(setStatus)
      .catch((err) => {
        setLoadError(
          err instanceof MaruyaApiError
            ? (err.body.message ?? err.message)
            : '状態の取得に失敗しました',
        )
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadStatus()
  }, [])

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (!loginId.trim() || !password || !subdomain.trim()) {
      setSubmitError('ログインID・パスワード・サブドメインをすべて入力してください')
      return
    }
    setSubmitError(null)
    setConfirmOpen(true)
  }

  async function handleConfirm() {
    setSubmitting(true)
    setSubmitError(null)
    try {
      const updated = await putRpaCredentials(loginId.trim(), password, subdomain.trim())
      setStatus(updated)
      setEditing(false)
      setConfirmOpen(false)
      setLoginId('')
      setPassword('')
      setSubdomain('')
    } catch (err) {
      setSubmitError(
        err instanceof MaruyaApiError
          ? (err.body.message ?? err.message)
          : '更新に失敗しました',
      )
      setConfirmOpen(false)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="page">
      <section className="card">
        <header>
          <h2>RPA連携アカウント</h2>
          <p className="muted">
            サイボウズ Office をRPAが読み取る際に使用するアカウント情報です。
            保存した値はこの画面には再表示されません。
          </p>
        </header>

        {loading && <p className="muted">読み込み中…</p>}
        {loadError && <p className="form-error">{loadError}</p>}

        {!loading && !loadError && !editing && (
          <>
            <dl className="meta-list">
              <div>
                <dt>状態</dt>
                <dd>{status?.configured ? '設定済み' : '未設定'}</dd>
              </div>
              {status?.configured && (
                <div>
                  <dt>最終更新</dt>
                  <dd>
                    {status.updated_at ?? '—'}
                    {status.updated_by ? `（${status.updated_by}）` : ''}
                  </dd>
                </div>
              )}
            </dl>
            <p className="actions-row">
              <button type="button" className="btn btn-primary" onClick={() => setEditing(true)}>
                {status?.configured ? '変更する' : '設定する'}
              </button>
            </p>
          </>
        )}

        {!loading && !loadError && editing && (
          <form className="form-card" onSubmit={handleSubmit}>
            <label className="field">
              <span>サブドメイン</span>
              <input
                type="text"
                value={subdomain}
                onChange={(event) => setSubdomain(event.target.value)}
                autoComplete="off"
                placeholder="例: acme（https://acme.cybozu.com）"
                required
              />
            </label>
            <label className="field">
              <span>ログインID</span>
              <input
                type="text"
                value={loginId}
                onChange={(event) => setLoginId(event.target.value)}
                autoComplete="off"
                required
              />
            </label>
            <label className="field">
              <span>パスワード</span>
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                required
              />
            </label>

            {submitError && <p className="form-error">{submitError}</p>}

            <div className="actions-row">
              <button type="submit" className="btn btn-primary">
                保存する
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setEditing(false)
                  setLoginId('')
                  setPassword('')
                  setSubdomain('')
                  setSubmitError(null)
                }}
              >
                キャンセル
              </button>
            </div>
          </form>
        )}
      </section>

      {confirmOpen && (
        <ConfirmDialog
          variant="approve"
          title="RPA連携アカウント情報を更新しますか？"
          message="この操作は取り消せません。入力した値はRPA側が次回の読み取り実行時に使用します。"
          confirmLabel="更新する"
          busy={submitting}
          error={submitError}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => void handleConfirm()}
        />
      )}
    </div>
  )
}
